"use client"

import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "@/hooks/useAuth"
import {
  getDiskUsage,
  getDiskUsageMetadata,
  type DiskUsageMetadataResponse,
  type DiskUsageResponse,
  type DiskUsageScope,
  type DiskUsageItem,
} from "@/services/resourceUsageService"
import { House } from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card.tsx"
import {
  formatSnapshotDate,
  estimateMonthlyCost,
} from "./diskUsageUtils"
import DiskUsageTableBody from "./DiskUsageTableBody"
import type { DiskUsageRow } from "./DiskUsageTableBody"
import { Button } from "@/components/ui/button"

const PAGE_TITLE = "Overall Resource Usage"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Navigation state for the admin view.
 * Levels: virtual root (scopes) → users within scope → directory tree.
 */
interface AdminNavState {
  /** Current scope, or null for the virtual root. */
  scope: DiskUsageScope | null
  /** Current user within the scope, or null if viewing user list. */
  user: string | null
  /** Path within the user's tree, or null for user root. */
  path: string | null
}

/**
 * Extended row used internally by AdminDiskUsageTable.
 * Adds `user` on top of the shared DiskUsageRow so the click handler can
 * distinguish between scope-level rows (user = null) and user-level rows.
 */
interface AdminTableRow extends DiskUsageRow {
  /** The user this item belongs to (for user-level and directory-level rows). */
  user: string | null
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * AdminDiskUsageTable component.
 *
 * Admin view of disk usage across all users. Shows:
 * 1. Virtual root: each scope with total size across all users
 * 2. Scope level: each user within the scope with their root size
 * 3. User level: standard directory tree navigation (unlimited depth)
 */
export default function AdminDiskUsageTable() {
  const { getAccessToken } = useAuth()
  const navigate = useNavigate()

  // Metadata
  const [metadata, setMetadata] = useState<DiskUsageMetadataResponse | null>(null)

  // Navigation
  const [nav, setNav] = useState<AdminNavState>({ scope: null, user: null, path: null })

  // Scope totals: { scope: total_bytes } — loaded progressively
  const [scopeTotals, setScopeTotals] = useState<Record<string, number | null>>({})

  // User roots within a scope: { user: cumdsize }
  const [userRoots, setUserRoots] = useState<Record<string, number | null>>({})

  // Current directory data (when inside a user's directory tree)
  const [currentData, setCurrentData] = useState<DiskUsageResponse | null>(null)

  // Loading states
  const [initialLoading, setInitialLoading] = useState<boolean>(true)
  const [navigating, setNavigating] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * Fetch metadata on mount. Then compute scope totals by fetching each
   * user's root concurrently.
   */
  useEffect(() => {
    async function init(): Promise<void> {
      try {
        const accessToken = await getAccessToken()
        const meta = await getDiskUsageMetadata(accessToken)
        setMetadata(meta)

        if (!meta.users_available) {
          setError("Admin access required. No user data available.")
          setInitialLoading(false)
          return
        }

        // Initialize scope totals as loading
        const initTotals: Record<string, number | null> = {}
        for (const scope of meta.scopes_available) {
          initTotals[scope] = null
        }
        setScopeTotals(initTotals)

        // Fetch all user roots per scope concurrently to compute totals
        for (const scope of meta.scopes_available) {
          const version = meta.latest[scope]
          const users = meta.users_available[scope] ?? []
          if (!version || users.length === 0) {
            setScopeTotals(prev => ({ ...prev, [scope]: 0 }))
            continue
          }

          // Fetch each user's root in parallel
          const userPromises = users.map(async user => {
            try {
              const data = await getDiskUsage({ scope, version, user }, accessToken)
              return data.cumdsize
            } catch {
              return 0
            }
          })

          Promise.all(userPromises).then(sizes => {
            const total = sizes.reduce((sum, s) => sum + s, 0)
            setScopeTotals(prev => ({ ...prev, [scope]: total }))
          })
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to load data"
        setError(message)
      } finally {
        setInitialLoading(false)
      }
    }

    init()
  }, [getAccessToken])

  /**
   * Load user roots when navigating into a scope (showing user list).
   *
   * @param scope - The scope to load user roots for.
   */
  const loadUserRoots = useCallback(
    async (scope: DiskUsageScope): Promise<void> => {
      if (!metadata?.users_available) return
      const version = metadata.latest[scope]
      const users = metadata.users_available[scope] ?? []
      if (!version) return

      // Initialize all as loading
      const initRoots: Record<string, number | null> = {}
      for (const user of users) {
        initRoots[user] = null
      }
      setUserRoots(initRoots)

      const accessToken = await getAccessToken()

      // Fetch each user's root concurrently
      for (const user of users) {
        getDiskUsage({ scope, version, user }, accessToken)
          .then(data => {
            setUserRoots(prev => ({ ...prev, [user]: data.cumdsize }))
          })
          .catch(() => {
            setUserRoots(prev => ({ ...prev, [user]: 0 }))
          })
      }
    },
    [metadata, getAccessToken]
  )

  /**
   * Navigate into a scope (show users list).
   *
   * @param scope - The scope to navigate into.
   */
  function handleScopeClick(scope: DiskUsageScope): void {
    setNav({ scope, user: null, path: null })
    setCurrentData(null)
    loadUserRoots(scope)
  }

  /**
   * Navigate into a user's directory tree at a specific path.
   *
   * @param scope - The scope containing the user.
   * @param user - The user to navigate into.
   * @param path - The path within the user's tree (null for root).
   */
  const navigateToUserPath = useCallback(
    async (scope: DiskUsageScope, user: string, path: string | null): Promise<void> => {
      if (!metadata) return

      const version = metadata.latest[scope]
      if (!version) return

      setNavigating(true)
      setError(null)

      try {
        const accessToken = await getAccessToken()
        const data = await getDiskUsage(
          {
            scope,
            version,
            user,
            path: path ?? undefined,
          },
          accessToken
        )
        setCurrentData(data)
        setNav({ scope, user, path })
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to load directory"
        setError(message)
      } finally {
        setNavigating(false)
      }
    },
    [metadata, getAccessToken]
  )

  /**
   * Handle clicking on a user row to enter their directory tree.
   *
   * @param scope - The scope the user belongs to.
   * @param user - The user to navigate into.
   */
  function handleUserClick(scope: DiskUsageScope, user: string): void {
    navigateToUserPath(scope, user, null)
  }

  /**
   * Handle clicking on a directory row within a user's tree.
   *
   * @param itemPath - The path to navigate to.
   */
  function handleDirectoryClick(itemPath: string): void {
    if (!nav.scope || !nav.user) return
    navigateToUserPath(nav.scope, nav.user, itemPath)
  }

  /**
   * Navigate up one level.
   * - From a subdirectory: go to parent directory
   * - From user root: go back to user list
   * - From user list: go back to virtual root
   */
  function navigateUp(): void {
    if (nav.scope === null) return // Already at virtual root

    if (nav.user !== null) {
      // Inside a user's directory tree
      if (nav.path === null || nav.path === "/") {
        // At user root → go back to user list
        setNav({ scope: nav.scope, user: null, path: null })
        setCurrentData(null)
        loadUserRoots(nav.scope)
      } else {
        // Go to parent path within the user's tree
        const parts = nav.path.replace(/^\//, "").split("/")
        parts.pop()
        const parentPath = parts.length === 0 ? null : "/" + parts.join("/")
        navigateToUserPath(nav.scope, nav.user, parentPath)
      }
    } else {
      // At user list → go to virtual root
      setNav({ scope: null, user: null, path: null })
      setCurrentData(null)
    }
  }

  // ---------------------------------------------------------------------------
  // Build rows
  // ---------------------------------------------------------------------------

  /**
   * Build table rows based on the current navigation state.
   *
   * @returns An array of AdminTableRow objects to render.
   */
  function buildRows(): AdminTableRow[] {
    // Virtual root: show scopes
    if (nav.scope === null) {
      if (!metadata) return []
      return metadata.scopes_available.map(scope => ({
        name: scope,
        isDirectory: true,
        size: scopeTotals[scope] ?? 0,
        loading: scopeTotals[scope] === null,
        scope,
        user: null,
        navigatePath: null,
      }))
    }

    // Scope level: show users (clickable to enter their tree)
    if (nav.user === null) {
      const users = metadata?.users_available?.[nav.scope] ?? []
      return users.map(user => ({
        name: user,
        isDirectory: true, // Users are now clickable directories
        size: userRoots[user] ?? 0,
        loading: userRoots[user] === null,
        scope: nav.scope,
        user,
        navigatePath: null,
      }))
    }

    // User level: show directory contents from currentData
    if (!currentData) return []

    return currentData.contents.map((item: DiskUsageItem) => {
      const size = item.isDirectory ? (item.cumdsize ?? 0) : item.dsize
      const itemPath =
        currentData.path === "/" ? `/${item.name}` : `${currentData.path}/${item.name}`

      return {
        name: item.name,
        isDirectory: item.isDirectory,
        size,
        loading: false,
        scope: nav.scope,
        user: nav.user,
        navigatePath: itemPath,
      }
    })
  }

  const rows = buildRows()
  const sortedRows = [...rows].sort((a, b) => b.size - a.size)
  const maxSize = sortedRows.reduce((sum, row) => sum + row.size, 0) || 1

  // ---------------------------------------------------------------------------
  // Breadcrumbs
  // ---------------------------------------------------------------------------

  /**
   * Build clickable breadcrumb segments from the current navigation state.
   * Each segment can be clicked to navigate directly to that level.
   *
   * @returns An array of breadcrumb objects with label and onClick handler.
   */
  function buildBreadcrumbs(): { label: string; onClick: () => void; isRoot?: boolean }[] {
    const crumbs: { label: string; onClick: () => void; isRoot?: boolean }[] = []

    // Root is always first
    crumbs.push({
      label: "__root__",
      isRoot: true,
      onClick: () => {
        setNav({ scope: null, user: null, path: null })
        setCurrentData(null)
      },
    })

    if (nav.scope === null) return crumbs

    // Scope level
    crumbs.push({
      label: nav.scope,
      onClick: () => handleScopeClick(nav.scope!),
    })

    if (nav.user === null) return crumbs

    // User level
    crumbs.push({
      label: nav.user,
      onClick: () => navigateToUserPath(nav.scope!, nav.user!, null),
    })

    // Path segments within the user's tree
    if (nav.path && nav.path !== "/") {
      const parts = nav.path.replace(/^\//, "").split("/")
      for (let i = 0; i < parts.length; i++) {
        const partPath = "/" + parts.slice(0, i + 1).join("/")
        const part = parts[i]
        crumbs.push({
          label: part,
          onClick: () => navigateToUserPath(nav.scope!, nav.user!, partPath),
        })
      }
    }

    return crumbs
  }

  const breadcrumbs = buildBreadcrumbs()

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------


  if (error && !metadata) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-destructive">{error}</p>
      </div>
    )
  }

  if (metadata && !metadata.users_available) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2">
        <p className="text-lg font-semibold">Access Denied</p>
        <p className="text-sm text-muted-foreground">
          This page is only available to administrators.
        </p>
      </div>
    )
  }

  return (
    <>
    <title>{PAGE_TITLE} - MAP Apps Platform</title>
    <Card className="flex flex-1 flex-col min-h-0 shadow-none gap-4">
      <CardHeader className="pb-0">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>
              <h1>{PAGE_TITLE}</h1>
            </CardTitle>
            <CardDescription>
              <p className="text-sm text-muted-foreground mt-1">
                All users
                {metadata &&
                  (() => {
                    const version = nav.scope
                      ? metadata.latest[nav.scope]
                      : Object.values(metadata.latest).find(Boolean)
                    return version ? <> · Snapshot: {formatSnapshotDate(version)}</> : null
                  })()}
              </p>
            </CardDescription>
          </div>
          {/* Navigate back to the personal disk usage view */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/computing/resource-usage/disk")}
          >
            My Usage
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col min-h-0 gap-2">
        {/* Breadcrumb */}
        <div>
          <p className="text-sm font-mono flex flex-wrap items-end gap-0.5 leading-none">
            {breadcrumbs.map((crumb, i) => {
              const isLast = i === breadcrumbs.length - 1
              const display = crumb.isRoot ? <House className="h-4 w-4" /> : crumb.label
              return (
                <span key={i} className="flex items-center gap-0.5">
                  {i > 0 && <span className="text-muted-foreground">/</span>}
                  {isLast ? (
                    <span className="text-foreground">{display}</span>
                  ) : (
                    <button onClick={crumb.onClick} className="text-primary hover:underline">
                      {display}
                    </button>
                  )}
                </span>
              )
            })}
          </p>
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        {/* Table */}
        <DiskUsageTableBody
          rows={sortedRows}
          maxSize={maxSize}
          initialLoading={initialLoading}
          navigating={navigating}
          isAtRoot={nav.scope === null}
          costColumnLabel="Monthly Cost"
          estimateCost={estimateMonthlyCost}
          onNavigateUp={navigateUp}
          onRowClick={(row: DiskUsageRow) => {
            // Cast to AdminTableRow to access the `user` field set by buildRows()
            const adminRow = row as AdminTableRow
            if (nav.scope === null && adminRow.scope) {
              // At virtual root: clicking a scope
              handleScopeClick(adminRow.scope)
            } else if (nav.user === null && adminRow.scope && adminRow.user) {
              // At scope level: clicking a user
              handleUserClick(adminRow.scope, adminRow.user)
            } else if (nav.user !== null && adminRow.navigatePath) {
              // Inside a user's tree: clicking a directory
              handleDirectoryClick(adminRow.navigatePath)
            }
          }}
        />
      </CardContent>
    </Card>
    </>
  )
}
